import React, {
  useEffect,
  useState,
  useRef,
  useCallback
} from "react";
import {
  useParams,
  useNavigate,
  useLocation
} from "react-router-dom";
import { toast } from "react-toastify";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  DecoupledEditor,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  BlockToolbar,
  Bold,
  Bookmark,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  FullPage,
  Fullscreen,
  Heading,
  Highlight,
  HorizontalLine,
  ImageBlock,
  ImageCaption,
  ImageEditing,
  ImageInline,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  ImageUtils,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  PlainTableOutput,
  RemoveFormat,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableLayout,
  TableProperties,
  TableToolbar,
  TextPartLanguage,
  TextTransformation,
  TodoList,
  Underline,
  WordCount
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import "ckeditor5-premium-features/ckeditor5-premium-features.css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import SummarySidebar from "./SummarySidebar";
import {
  negotiationDetails,
  resolveNegotiationComment,
  saveNegotiationNewVersion,
  sendNegotiation,
  Versioncomparesion,
  sendNegotiationMessage,
  negotiationMessageList
} from "../../../../services/CommonService";
import "../ManageNegotiation.css";
import VersionComparisonTab from "./NegotiationVersionComparison";
import Utils from "../../../../utils";
import { FaTimes, FaBars } from "react-icons/fa";
import ModalComponent from "../../../../components/ModalComponent";

const LICENSE_KEY = process.env.REACT_APP_CKEDITOR_LICENSE_KEY;

const MANAGE_HIGHLIGHT_COLORS = [
  { bg: "#ffd365", border: "#FBC02D", name: "yellow" },
  { bg: "#c8e6c9", border: "#66bb6a", name: "green" }
];

// ✅ Add ids to all elements to keep stable mapping
const ensureManageElementIds = (htmlString) => {
  if (!htmlString) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const elements = doc.querySelectorAll("*");
  let counter = 0;

  elements.forEach((el) => {
    if (!el.id) el.id = `element-auto-${counter++}`;
  });

  return doc.body.innerHTML;
};

const hasCommentTextChanged = (comment, oldHTML, newHTML) => {
  const selected = comment.position.selectedText?.trim();
  if (!selected) return false;

  // FULL CLEANER – removes HTML + decodes inline splits
  const normalize = (html) => {
    return html
      .replace(/<[^>]+>/g, " ")       // remove tags
      .replace(/&nbsp;/g, " ")        // remove html spaces
      .replace(/\s+/g, " ")           // collapse spaces
      .trim()
      .toLowerCase();                 // normalize case
  };

  const oldClean = normalize(oldHTML);
  const newClean = normalize(newHTML);
  const selectedClean = selected.trim().toLowerCase();

  // 🔥 NEW LOGIC: check if NEW contains SAME WORD as OLD
  const oldHas = oldClean.includes(selectedClean);
  const newHas = newClean.includes(selectedClean);

  // If OLD had the word but NEW still has it → no modification
  if (oldHas && newHas) return false;

  // If OLD had it but NEW removed/changed it → modified
  if (oldHas && !newHas) return true;

  return false;
};

const NegotiationManage = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // 🔹 Single combined state for local UI
  const [state, setState] = useState({
    content: "",
    originalContent: "",
    comments: [],
    loading: false,
    saving: false,
    activeCommentId: null,
    versionSaved: false,
    activeTab: "draft",
    selectedRecipientId: null,
    commentFilter: "all",
    comparisonData: null,
    versionLoading: false,
    showComparison: false,
    isRecipientDropdownOpen: false,
    isSidebarOpen: false,
    isModalOpen: false,
    previewPages: [],
    isDropdownOpen: false,
    commentMessages: {},
    replyInputs: {},
    openReplyBox: {},
    loadingMessages: {},
  });

  const updateState = (patch) =>
    setState((prev) => ({
      ...prev,
      ...patch
    }));

  const {
    content,
    comments,
    loading,
    activeCommentId,
    versionSaved,
    activeTab,
    selectedRecipientId,
    commentFilter,
    comparisonData,
    versionLoading,
    showComparison,
    isRecipientDropdownOpen,
    isSidebarOpen,
    isModalOpen,
    previewPages,
    isDropdownOpen
  } = state;

  // CKEditor refs
  const manageEditorRef = useRef(null);
  const manageEditorContainerRef = useRef(null);
  const manageToolbarRef = useRef(null);
  const manageMenuBarRef = useRef(null);
  const manageWordCountRef = useRef(null);
  const manageHighlightsAppliedRef = useRef(false);

  const initialNegotiation = location.state?.negotiation || undefined;

  // 🔹 Negotiation details via React Query (fetch on mount)
  const {
    data: manageNegotiation,
    isLoading: negotiationLoading
  } = useQuery({
    queryKey: ["negotiation-manage", uuid],
    queryFn: async () => {
      const response = await negotiationDetails({ uuid });
      const data = response?.data?.data;
      if (!data) {
        throw new Error(
          response?.data?.message || "Failed to load negotiation details"
        );
      }
      return data;
    },
    enabled: !!uuid,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    onError: (error) => {
      toast.error(Utils.getErrorMessage(error));
      navigate("/client-portal/negotiation-contract");
    }
  });

  // 🔹 Load version comparison
  const loadVersionComparison = useCallback(async () => {
    if (!manageNegotiation?.id) return;

    updateState({ versionLoading: true });

    try {
      const response = await Versioncomparesion({
        negotiation_id: manageNegotiation.id
      });

      if (response?.data?.data) {
        updateState({
          comparisonData: response.data.data,
          showComparison: true
        });
        toast.success(response?.data?.message);
      } else {
        toast.error(response?.data?.message || "Failed to load versions");
        updateState({ comparisonData: null });
      }
    } catch (error) {
      toast.error(Utils.getErrorMessage(error));
      updateState({ comparisonData: null });
    } finally {
      updateState({ versionLoading: false });
    }
  }, [manageNegotiation?.id]);

  // 🔹 When switching to Versions tab load comparison once
  useEffect(() => {
    if (
      activeTab === "versions" &&
      manageNegotiation?.id &&
      !showComparison
    ) {
      loadVersionComparison();
    }
  }, [activeTab, manageNegotiation?.id, showComparison, loadVersionComparison]);

  // 🔹 Set content + comments once data loaded
  useEffect(() => {
    if (manageNegotiation) {
      const htmlContent = ensureManageElementIds(manageNegotiation.html_content || "");
      updateState({
        content: htmlContent,
        originalContent: htmlContent,
        comments: manageNegotiation.comments || [],
        versionSaved: true
      });
    }
  }, [manageNegotiation, uuid]);

  // 🔹 Reset highlight flag when content changes
  useEffect(() => {
    manageHighlightsAppliedRef.current = false;
  }, [content]);

  // 🔹 Apply highlights in CKEditor based on comment offsets
  const applyManageHighlights = useCallback(() => {
    const editor = manageEditorRef.current;
    if (!editor || !comments?.length) return;

    const model = editor.model;
    const root = model.document.getRoot();

    setTimeout(() => {
      model.enqueueChange({ isUndoable: false }, (writer) => {
        try {
          const startAll = model.createPositionAt(root, 0);
          const endAll = model.createPositionAt(root, "end");
          const fullRange = writer.createRange(startAll, endAll);

          // Remove existing highlights
          writer.removeAttribute("manageHighlight", fullRange);

          const convertOffsetToPosition = (offset) => {
            let currentNonWhitespacePos = 0;

            for (const block of root.getChildren()) {
              if (!block.is("element")) continue;

              for (const node of block.getChildren()) {
                if (!node.is("text")) continue;

                const originalText = node.data;
                let nonWhitespaceCount = 0;

                for (let i = 0; i < originalText.length; i++) {
                  const char = originalText[i];

                  if (!/\s/.test(char)) {
                    if (currentNonWhitespacePos + nonWhitespaceCount === offset) {
                      return writer.createPositionAt(
                        node.parent,
                        node.startOffset + i
                      );
                    }
                    nonWhitespaceCount++;
                  }
                }

                currentNonWhitespacePos += nonWhitespaceCount;

                if (currentNonWhitespacePos >= offset) {
                  return writer.createPositionAt(
                    node.parent,
                    node.startOffset + originalText.length
                  );
                }
              }
            }

            return null;
          };

          comments.forEach((comment) => {
            const { start_offset, end_offset } = comment.position || {};
            if (
              start_offset === undefined ||
              end_offset === undefined
            )
              return;

            const startPos = convertOffsetToPosition(start_offset);
            const endPos = convertOffsetToPosition(end_offset);
            if (!startPos || !endPos) return;

            const range = writer.createRange(startPos, endPos);
            const colorIndex = comment.is_resolved ? 1 : 0; // 0 = yellow (unresolved), 1 = green (resolved)

            writer.setAttribute("manageHighlight", `${colorIndex}`, range);
          });

          manageHighlightsAppliedRef.current = true;
        } catch (error) {
          console.error("Error applying highlights:", error);
        }
      });
    }, 400);
  }, [comments]);

  // 🔹 Apply highlights once editor + comments ready
  useEffect(() => {
    if (
      manageEditorRef.current &&
      comments?.length > 0 &&
      !manageHighlightsAppliedRef.current
    ) {
      applyManageHighlights();
    }
  }, [comments, applyManageHighlights]);

  // 🔹 Scroll to highlighted section when clicking comment card
  const scrollToManageHighlight = useCallback(
    async (commentId) => {
      const comment = comments.find((c) => c.id === commentId);
      if (!comment) return false;

      updateState({ activeCommentId: commentId });

      const editor = manageEditorRef.current;
      if (!editor) return false;

      const model = editor.model;
      const root = model.document.getRoot();

      try {
        const convertOffsetToPositionScroll = (offset) => {
          let currentNonWhitespacePos = 0;

          for (const block of root.getChildren()) {
            if (!block.is("element")) continue;

            for (const node of block.getChildren()) {
              if (!node.is("text")) continue;

              const originalText = node.data;
              let nonWhitespaceCount = 0;

              for (let i = 0; i < originalText.length; i++) {
                const char = originalText[i];

                if (!/\s/.test(char)) {
                  if (currentNonWhitespacePos + nonWhitespaceCount === offset) {
                    return model.createPositionAt(
                      node.parent,
                      node.startOffset + i
                    );
                  }
                  nonWhitespaceCount++;
                }
              }

              currentNonWhitespacePos += nonWhitespaceCount;

              if (currentNonWhitespacePos >= offset) {
                return model.createPositionAt(
                  node.parent,
                  node.startOffset + originalText.length
                );
              }
            }
          }

          return null;
        };

        const startPos = convertOffsetToPositionScroll(
          comment.position.start_offset
        );
        const endPos = convertOffsetToPositionScroll(
          comment.position.end_offset
        );

        if (!startPos || !endPos) {
          console.warn("Positions not found");
          return false;
        }

        model.change((writer) => {
          const targetRange = model.createRange(startPos, endPos);
          writer.setSelection(targetRange);
        });

        await new Promise((resolve) => setTimeout(resolve, 100));

        const editableElement = document.querySelector(".ck-editor__editable");
        if (!editableElement) {
          console.warn("Editable element not found");
          return false;
        }

        const editingView = editor.editing.view;
        const viewRange = editor.editing.mapper.toViewRange(
          model.createRange(startPos, endPos)
        );
        const domRange =
          editingView.domConverter.viewRangeToDom(viewRange);
        if (!domRange) return false;

        let targetElement = domRange.commonAncestorContainer;
        if (targetElement.nodeType === Node.TEXT_NODE) {
          targetElement = targetElement.parentElement;
        }
        if (!targetElement) {
          console.warn("Target element not found");
          return false;
        }

        const elementRect = targetElement.getBoundingClientRect();
        const editableRect = editableElement.getBoundingClientRect();

        editableElement.parentElement.scrollTop = 0;

        setTimeout(() => {
          editableElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });

          const parentContainer =
            editableElement.closest(".manage-ckeditor-wrapper");
          if (parentContainer) {
            const scrollPos =
              elementRect.top - editableRect.top - 100;
            parentContainer.scrollTo({
              top: Math.max(0, scrollPos),
              behavior: "smooth"
            });
          }
        }, 50);

        editingView.focus();

        setTimeout(() => {
          updateState({ activeCommentId: null });
        }, 2000);

        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    [comments]
  );

  const generateA4Pages = useCallback((html) => {
    updateState({ loading: true, previewPages: [] });

    if (!html) {
      updateState({ loading: false, previewPages: [] });
      return;
    }

    setTimeout(async () => {
      const mmToPx = (mm) => Math.round(mm * 3.78);
      const PADDING_MM = 15;
      const CONTENT_HEIGHT_MM = 297 - PADDING_MM * 2;
      const contentHeightPx = mmToPx(CONTENT_HEIGHT_MM);

      const tempRoot = document.createElement("div");
      tempRoot.style.position = "absolute";
      tempRoot.style.left = "-9999px";
      tempRoot.style.top = "-9999px";
      tempRoot.style.width = "210mm";
      tempRoot.style.padding = "15mm";
      tempRoot.style.visibility = "hidden";
      tempRoot.style.boxSizing = "border-box";
      tempRoot.innerHTML = html;

      document.body.appendChild(tempRoot);

      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));

      const pages = [];
      let currentPageNodes = [];
      let currentHeight = 0;

      const nodes = Array.from(tempRoot.childNodes);

      for (const node of nodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        const measure = document.createElement("div");
        measure.style.width = "210mm";
        measure.style.padding = "15mm";
        measure.style.position = "absolute";
        measure.style.visibility = "hidden";
        measure.style.boxSizing = "border-box";

        const clone = node.cloneNode(true);
        measure.appendChild(clone);
        document.body.appendChild(measure);

        await new Promise((r) => requestAnimationFrame(r));

        const blockHeight = measure.offsetHeight - mmToPx(PADDING_MM * 2);
        document.body.removeChild(measure);

        if (currentHeight + blockHeight > contentHeightPx && currentPageNodes.length > 0) {
          pages.push(currentPageNodes);
          currentPageNodes = [];
          currentHeight = 0;
        }

        currentPageNodes.push(node.cloneNode(true));
        currentHeight += blockHeight;
      }

      if (currentPageNodes.length) {
        pages.push(currentPageNodes);
      }

      const pageHtmlList = pages.map((els) => {
        const pageDiv = document.createElement("div");
        pageDiv.style.width = "210mm";
        pageDiv.style.minHeight = "297mm";
        pageDiv.style.padding = "20mm";
        pageDiv.style.background = "#fff";
        pageDiv.style.boxSizing = "border-box";
        pageDiv.style.margin = "10px auto";
        pageDiv.style.borderRadius = "6px";
        pageDiv.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
        pageDiv.style.lineHeight = "1.6";
        pageDiv.style.color = "#000";

        els.forEach((el) => pageDiv.appendChild(el));
        return pageDiv.innerHTML;
      });

      updateState({
        previewPages: pageHtmlList,
        loading: false
      });

      document.body.removeChild(tempRoot);
    }, 40);
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      generateA4Pages(content);
    }
  }, [isModalOpen, content, generateA4Pages]);

  const handleSaveNewVersion = async () => {
    if (!manageNegotiation) return;

    updateState({ loading: true });

    try {
      const response = await saveNegotiationNewVersion({
        negotiation_id: manageNegotiation.id,
        html_content: content
      });

      // If we get a response without error, consider it successful
      if (response?.data) {
        toast.success(
          response?.data?.message || "Version saved successfully!"
        );
        updateState({ versionSaved: true, originalContent: content });
        await queryClient.invalidateQueries([
          "negotiation-manage",
          uuid
        ]);
      } else {
        toast.error("Failed to save version");
      }
    } catch (error) {
      toast.error(Utils.getErrorMessage(error));
    } finally {
      updateState({ loading: false });
    }
  };

  const handleManageResolveComment = async (commentId) => {
    if (!manageNegotiation) return;

    updateState({ loading: true });

    try {
      const comment = comments.find((c) => c.id === commentId);
      if (!comment) {
        toast.error("Comment not found");
        return;
      }

      const oldHTML = manageNegotiation.html_content;
      const newHTML = content;

      const modified = hasCommentTextChanged(
        comment,
        oldHTML,
        newHTML
      );

      // if (!modified) {
      //   toast.error(
      //     "Please update the highlighted text before resolving."
      //   );
      //   return;
      // }

      const response = await resolveNegotiationComment({
        negotiation_id: manageNegotiation.id,
        comment_id: commentId
      });

      toast.success(
        response?.data?.message || "Comment resolved!"
      );

      const updatedComments = comments.map((c) =>
        c.id === commentId ? { ...c, is_resolved: true } : c
      );

      updateState({ comments: updatedComments });

      setTimeout(() => {
        applyManageHighlights();
      }, 300);
    } catch (error) {
      toast.error(Utils.getErrorMessage(error));
    } finally {
      updateState({ loading: false });
    }
  };

  // 🔹 Send negotiation
  const handleSendNegotiation = async () => {
    if (!manageNegotiation) return;

    updateState({ loading: true });

    try {
      const response = await sendNegotiation({
        negotiation_id: manageNegotiation.id
      });
      toast.success(
        response?.data?.message || "Negotiation sent successfully!"
      );
    } catch (error) {
      toast.error(Utils.getErrorMessage(error));
    } finally {
      updateState({ loading: false });
    }
  };

  // 🔹 Invalid uuid guard
  useEffect(() => {
    if (!uuid) {
      toast.error("Invalid negotiation ID");
      navigate("/client-portal/negotiation-contract");
    }
  }, [uuid, navigate]);

  if (!manageNegotiation) return null;

  const { negotiation_name } = manageNegotiation;
  const { current_version } = manageNegotiation;
  const globalLoading = loading || negotiationLoading;

  // 🔹 Filter comments by recipient + status
  const filteredComments = comments.filter((comment) => {
    if (
      selectedRecipientId &&
      comment.recipient_id !== selectedRecipientId
    )
      return false;
    if (commentFilter === "resolved") return comment.is_resolved;
    if (commentFilter === "unresolved") return !comment.is_resolved;
    return true;
  });

  // 🔹 CKEditor config
  const manageEditorConfig = {
    toolbar: {
      items: [
        "undo",
        "redo",
        "|",
        "heading",
        "|",
        "fontSize",
        "fontFamily",
        "fontColor",
        "fontBackgroundColor",
        "|",
        "bold",
        "italic",
        "underline",
        "|",
        "link",
        "insertTable",
        "highlight",
        "blockQuote",
        "|",
        "alignment",
        "|",
        "bulletedList",
        "numberedList",
        "todoList",
        "outdent",
        "indent",
        "pageBreak"
      ],
      shouldNotGroupWhenFull: false
    },
    plugins: [
      Alignment,
      Autoformat,
      AutoImage,
      AutoLink,
      Autosave,
      BalloonToolbar,
      BlockQuote,
      BlockToolbar,
      Bold,
      Bookmark,
      Essentials,
      FindAndReplace,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      FullPage,
      Fullscreen,
      Heading,
      Highlight,
      HorizontalLine,
      ImageBlock,
      ImageCaption,
      ImageEditing,
      ImageInline,
      ImageUtils,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      ListProperties,
      PageBreak,
      Paragraph,
      PasteFromOffice,
      PlainTableOutput,
      RemoveFormat,
      SpecialCharacters,
      SpecialCharactersArrows,
      SpecialCharactersCurrency,
      SpecialCharactersEssentials,
      SpecialCharactersLatin,
      SpecialCharactersMathematical,
      SpecialCharactersText,
      Strikethrough,
      Subscript,
      Superscript,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextPartLanguage,
      TextTransformation,
      TodoList,
      Underline,
      WordCount
    ],
    balloonToolbar: [
      "bold",
      "italic",
      "|",
      "link",
      "|",
      "bulletedList",
      "numberedList"
    ],
    blockToolbar: [
      "fontSize",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "bold",
      "italic",
      "|",
      "link",
      "insertTable",
      "|",
      "bulletedList",
      "numberedList",
      "outdent",
      "indent"
    ],
    fontFamily: { supportAllValues: true },
    fontSize: {
      options: [10, 12, 14, "default", 18, 20, 22],
      supportAllValues: true
    },
    heading: {
      options: [
        {
          model: "paragraph",
          title: "Paragraph",
          class: "ck-heading_paragraph"
        },
        {
          model: "heading1",
          view: "h1",
          title: "Heading 1",
          class: "ck-heading_heading1"
        },
        {
          model: "heading2",
          view: "h2",
          title: "Heading 2",
          class: "ck-heading_heading2"
        },
        {
          model: "heading3",
          view: "h3",
          title: "Heading 3",
          class: "ck-heading_heading3"
        },
        {
          model: "heading4",
          view: "h4",
          title: "Heading 4",
          class: "ck-heading_heading4"
        },
        {
          model: "heading5",
          view: "h5",
          title: "Heading 5",
          class: "ck-heading_heading5"
        },
        {
          model: "heading6",
          view: "h6",
          title: "Heading 6",
          class: "ck-heading_heading6"
        }
      ]
    },
    highlight: {
      options: MANAGE_HIGHLIGHT_COLORS.map((color, index) => ({
        model: `manage-highlight-${index}-builtin`,
        class: `marker-${color.name}`,
        title:
          color.name.charAt(0).toUpperCase() + color.name.slice(1),
        color: `var(--ck-highlight-marker-${color.name})`,
        type: "marker"
      }))
    },
    licenseKey: LICENSE_KEY
  };

  // Fetch replies for a comment
  const fetchCommentMessages = async (commentId) => {
    try {
      updateState({
        loadingMessages: { ...state.loadingMessages, [commentId]: true }
      });

      const res = await negotiationMessageList({
        comment_id: commentId,
        uuid // drafter has login_id header, recipients have uuid
      });

      updateState({
        commentMessages: {
          ...state.commentMessages,
          [commentId]: res?.data?.data || []
        },
        loadingMessages: { ...state.loadingMessages, [commentId]: false }
      });

    } catch (err) {
      updateState({
        loadingMessages: { ...state.loadingMessages, [commentId]: false }
      });
    }
  };

  // Send reply
  const handleSendReply = async (commentId) => {
    const text = state.replyInputs[commentId];
    if (!text?.trim()) return;

    try {
      await sendNegotiationMessage({
        comment_id: commentId,
        message_text: text,
        uuid
      });

      updateState({
        replyInputs: { ...state.replyInputs, [commentId]: "" }
      });

      fetchCommentMessages(commentId);
    } catch (err) { }
  };


  return (
    <>
      {globalLoading && (
        <div className="page-loading">
          <img src="/images/loader.gif" alt="Loading..." />
        </div>
      )}

      <div className="manage-negotiate-layout">
        {/* HEADER */}
        <header className="manage-negotiate-header-clean">
          <div className="manage-negotiate-header-clean-content">
            <button
              className="manage-negotiate-back-btn"
              onClick={() => navigate(-1)}
            >
              <i className="bi bi-arrow-left" />
            </button>
            <div className="d-flex align-items-center gap-3">
              <p className="manage-negotiate-header-version">v{current_version}</p>
              <h5 className="manage-negotiate-header-title fw-bold">
                {negotiation_name}
              </h5>
            </div>
            <button
              className={`sidebar-toggle-btn-ui ${isSidebarOpen ? "active" : ""
                }`}
              onClick={() =>
                updateState({ isSidebarOpen: !isSidebarOpen })
              }
            >
              {isSidebarOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </header>

        {/* TABS */}
        <div className="manage-negotiate-tabs-container">
          <nav className="manage-negotiate-tabs">
            <button
              className={`manage-negotiate-tab ${activeTab === "draft" ? "active" : ""
                }`}
              onClick={() => updateState({ activeTab: "draft" })}
            >
              <i className="bi bi-file-text" /> Draft
            </button>

            {manageNegotiation?.summary && (
              <button
                className={`manage-negotiate-tab ${activeTab === "summary" ? "active" : ""
                  }`}
                onClick={() => updateState({ activeTab: "summary" })}
              >
                <i className="bi bi-file-earmark-text" /> Summary
              </button>
            )}

            {manageNegotiation?.current_version > 1 && (
              <button
                className={`manage-negotiate-tab ${activeTab === "versions" ? "active" : ""
                  }`}
                onClick={() => updateState({ activeTab: "versions" })}
              >
                <i className="bi bi-diagram-3" /> Versions
              </button>
            )}
          </nav>
        </div>

        {/* MAIN BODY */}
        <div className="manage-negotiate-body">
          <div className="manage-negotiate-editor-panel">
            {/* ===== DRAFT TAB ===== */}
            {activeTab === "draft" && (
              <div className="manage-negotiate-tab-content manage-draft-panel">
                <div className="draft-content-wrapper">
                  {/* LEFT: CKEditor */}
                  <div className="draft-editor-section">
                    {content ? (
                      <div className="manage-negotiate-editor-container">
                        <div
                          className="manage-ckeditor-container"
                          ref={manageEditorContainerRef}
                        >
                          <div
                            className="manage-ckeditor-toolbar"
                            ref={manageToolbarRef}
                          />
                          <div
                            className="manage-ckeditor-menubar"
                            ref={manageMenuBarRef}
                          />

                          <div className="manage-ckeditor-wrapper">
                            <div className="manage-ckeditor-content">
                              <CKEditor
                                editor={DecoupledEditor}
                                config={manageEditorConfig}
                                data={content}
                                onReady={(editor) => {
                                  const editingView =
                                    editor.editing.view;

                                  editingView.change((writer) => {
                                    writer.setStyle(
                                      "width",
                                      "210mm",
                                      editingView.document.getRoot()
                                    );
                                    writer.setStyle(
                                      "min-height",
                                      "297mm",
                                      editingView.document.getRoot()
                                    );
                                    writer.setStyle(
                                      "padding",
                                      "21mm",
                                      editingView.document.getRoot()
                                    );
                                  });

                                  const wordCount =
                                    editor.plugins.get("WordCount");
                                  if (
                                    manageWordCountRef.current &&
                                    wordCount?.wordCountContainer
                                  ) {
                                    manageWordCountRef.current.appendChild(
                                      wordCount.wordCountContainer
                                    );
                                  }
                                  if (manageToolbarRef.current) {
                                    manageToolbarRef.current.appendChild(
                                      editor.ui.view.toolbar.element
                                    );
                                  }
                                  if (
                                    manageMenuBarRef.current &&
                                    editor.ui.view.menuBarView
                                  ) {
                                    manageMenuBarRef.current.appendChild(
                                      editor.ui.view.menuBarView.element
                                    );
                                  }

                                  manageEditorRef.current = editor;

                                  try {
                                    // Custom highlight mapping
                                    editor.conversion
                                      .for("downcast")
                                      .attributeToElement({
                                        model: "manageHighlight",
                                        view: (
                                          value,
                                          { writer: viewWriter }
                                        ) => {
                                          if (
                                            value === null ||
                                            value === undefined
                                          )
                                            return null;
                                          const className =
                                            "manage-highlight-" +
                                            value;
                                          return viewWriter.createAttributeElement(
                                            "span",
                                            { class: className },
                                            { priority: 5 }
                                          );
                                        }
                                      });

                                    editor.conversion
                                      .for("upcast")
                                      .elementToAttribute({
                                        view: {
                                          name: "span",
                                          key: "class",
                                          value: (viewElement) => {
                                            const cls =
                                              viewElement.getAttribute(
                                                "class"
                                              ) || "";
                                            const m =
                                              cls.match(
                                                /manage-highlight-([a-z0-9\-]+)/
                                              );
                                            return m
                                              ? {
                                                key: "manageHighlight",
                                                value: m[1]
                                              }
                                              : false;
                                          }
                                        }
                                      });
                                  } catch (convError) {
                                    console.warn(
                                      "Conversion registration error:",
                                      convError
                                    );
                                  }

                                  setTimeout(() => {
                                    applyManageHighlights();
                                    manageHighlightsAppliedRef.current =
                                      true;
                                  }, 500);
                                }}
                                onChange={(e, editor) => {
                                  const data = editor.getData();
                                  updateState({ content: data });
                                  manageHighlightsAppliedRef.current =
                                    false;
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          className="editor_container__word-count"
                          ref={manageWordCountRef}
                        />
                      </div>
                    ) : (
                      <div className="manage-negotiate-empty-state">
                        <i className="bi bi-file-text" />
                        <p>No document found.</p>
                      </div>
                    )}
                  </div>

                  {/* RIGHT: Comments sidebar */}
                  <aside
                    className={`draft-comments-section offcanvas-sidebar ${isSidebarOpen ? "open" : ""
                      }`}
                  >
                    <div className="draft-comments-header">
                      <div className="comments-header-top">
                        <h6 className="draft-comments-title">
                          <i className="bi bi-chat-left" /> Comments
                        </h6>

                        {/* Recipient Dropdown – Show only if more than 1 */}
                        <div>
                          {manageNegotiation?.recipient_list?.length >
                            1 && (
                              <div className="recipient-dropdown-wrapper">
                                <button
                                  className="recipient-dropdown-trigger px-2 py-1 rounded"
                                  onClick={() =>
                                    updateState({
                                      isDropdownOpen: !isDropdownOpen
                                    })
                                  }
                                >
                                  <i className="bi bi-funnel" />
                                  <span>
                                    {selectedRecipientId
                                      ? manageNegotiation.recipient_list.find(
                                        (r) =>
                                          r.id ===
                                          selectedRecipientId
                                      )?.first_name
                                      : "All"}
                                  </span>
                                  <i
                                    className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"
                                      }`}
                                  />
                                </button>

                                {isDropdownOpen && (
                                  <>
                                    <div
                                      className="dropdown-overlay"
                                      onClick={() =>
                                        updateState({
                                          isDropdownOpen: false
                                        })
                                      }
                                    />

                                    <div className="recipient-dropdown-menu">
                                      {/* ALL option */}
                                      <button
                                        className={`recipient-dropdown-item ${selectedRecipientId === null
                                          ? "active"
                                          : ""
                                          }`}
                                        onClick={() => {
                                          updateState({
                                            selectedRecipientId: null,
                                            isDropdownOpen: false
                                          });
                                        }}
                                      >
                                        <span className="manage-recipient-avatar all-avatar">
                                          <i className="bi bi-people-fill" />
                                        </span>
                                        <div className="recipient-info">
                                          <span className="manage-recipient-name">
                                            All Collaborators
                                          </span>
                                        </div>
                                        <span className="manage-comment-count">
                                          {comments.length}
                                        </span>
                                      </button>

                                      {/* Individual Recipients */}
                                      {manageNegotiation.recipient_list.map(
                                        (recipient) => {
                                          const recipientCount =
                                            comments.filter(
                                              (c) =>
                                                c.recipient_id ===
                                                recipient.id
                                            ).length;

                                          return (
                                            <button
                                              key={recipient.id}
                                              className={`recipient-dropdown-item ${selectedRecipientId ===
                                                recipient.id
                                                ? "active"
                                                : ""
                                                }`}
                                              onClick={() =>
                                                updateState({
                                                  selectedRecipientId:
                                                    recipient.id,
                                                  isDropdownOpen: false
                                                })
                                              }
                                            >
                                              <span className="manage-recipient-avatar">
                                                {recipient.first_name?.charAt(
                                                  0
                                                )}
                                                {recipient.last_name?.charAt(
                                                  0
                                                )}
                                              </span>

                                              <div className="recipient-info">
                                                <span className="manage-recipient-name">
                                                  {recipient.first_name}{" "}
                                                  {
                                                    recipient.last_name
                                                  }
                                                </span>
                                              </div>

                                              {recipientCount > 0 && (
                                                <span className="manage-comment-count">
                                                  {recipientCount}
                                                </span>
                                              )}
                                            </button>
                                          );
                                        }
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                        </div>

                        {/* Toggle Sidebar Button */}
                        <div>
                          <button
                            className={`sidebar-toggle-btn-ui ${isSidebarOpen ? "active" : ""
                              }`}
                            onClick={() =>
                              updateState({
                                isSidebarOpen: !isSidebarOpen
                              })
                            }
                          >
                            {isSidebarOpen ? (
                              <FaTimes />
                            ) : (
                              <FaBars />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Comment filter */}
                      <div className="comment-status-filter">
                        <label className="comment-radio">
                          <input
                            type="radio"
                            name="commentFilter"
                            value="all"
                            checked={commentFilter === "all"}
                            onChange={() =>
                              updateState({ commentFilter: "all" })
                            }
                          />
                          <span>All</span>
                        </label>

                        <label className="comment-radio">
                          <input
                            type="radio"
                            name="commentFilter"
                            value="resolved"
                            checked={commentFilter === "resolved"}
                            onChange={() =>
                              updateState({
                                commentFilter: "resolved"
                              })
                            }
                          />
                          <span>Resolved</span>
                        </label>

                        <label className="comment-radio">
                          <input
                            type="radio"
                            name="commentFilter"
                            value="unresolved"
                            checked={commentFilter === "unresolved"}
                            onChange={() =>
                              updateState({
                                commentFilter: "unresolved"
                              })
                            }
                          />
                          <span>Unresolved</span>
                        </label>
                      </div>

                      {/* Highlight styles */}
                      <style>{`
                        :root {
                          --ck-highlight-marker-yellow: #ffd365;
                          --ck-highlight-marker-green: #c8e6c9;
                        }

                        /* Unresolved comments - YELLOW */
                        .ck-content .manage-highlight-0 {
                          background-color: #ffd365;
                          cursor: pointer;
                          transition: background-color 0.2s ease;
                        }

                        .ck-content .manage-highlight-0:hover {
                          background-color: #ffca00;
                        }

                        /* Resolved comments - GREEN ✅ */
                        .ck-content .manage-highlight-1 {
                          background-color: #c8e6c9;
                          cursor: pointer;
                          transition: background-color 0.2s ease;
                        }

                        .ck-content .manage-highlight-1:hover {
                          background-color: #a5d6a7;
                        }

                        .manage-negotiate-comment-card.manage-comment-active {
                          animation: manage-comment-pulse 0.6s ease-in-out 3;
                          background-color: rgba(99, 102, 241, 0.15) !important;
                          transform: scale(1.02);
                        }

                        @keyframes manage-comment-pulse {
                          0%, 100% {
                            transform: scale(1);
                            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                          }
                          50% {
                            transform: scale(1.02);
                            box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
                          }
                        }
                      `}</style>
                    </div>

                    {/* COMMENT LIST */}
                    <div className="draft-comments-list">
                      {filteredComments.length > 0 ? (
                        filteredComments.map((comment) => (
                          <div
                            key={comment.id}
                            className={`comment-card ${comment.is_resolved ? "resolved" : ""}`}
                          >
                            <div className="comment-main" onClick={() => scrollToManageHighlight(comment.id)}>
                              <div className="comment-header">
                                <span className="user-name">{comment.recipient_name}</span>
                                <span className="comment-date">{new Date(comment.created_at).toLocaleDateString()}</span>
                              </div>
                              <div className="comment-body">{comment.comment_text}</div>

                              <div className="d-flex justify-content-between" style={{ alignItems: "baseline" }}>
                                <div>
                                  <button
                                    className="reply-toggle-btn"
                                    onClick={() => {
                                      const isOpen = state.openReplyBox[comment.id];
                                      updateState({
                                        openReplyBox: { ...state.openReplyBox, [comment.id]: !isOpen }
                                      });
                                      if (!isOpen) fetchCommentMessages(comment.id);
                                    }}
                                  >
                                    <i class="bi bi-chat-dots"></i> {state.openReplyBox[comment.id] ? "Replies" : "Replies"}
                                  </button>
                                </div>
                                <div>
                                  {!comment.is_resolved ? (
                                    <button
                                      className="resolve-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleManageResolveComment(comment.id);
                                      }}
                                    >
                                      <i className="bi bi-check-circle" /> Mark As Resolve
                                    </button>
                                  ) : (
                                    <span className="resolved-badge">
                                      <i className="bi bi-check2" /> Resolved
                                    </span>
                                  )}</div>
                              </div>
                            </div>
                            {state.openReplyBox[comment.id] && (
                              <div className="reply-thread">
                                <div className="reply-list">
                                  {(state.commentMessages[comment.id] || []).map((m) => (
                                    <div
                                      key={m.id}
                                      className={`reply-item`}
                                    >
                                      <div className="reply-bubble">
                                        <div className="reply-top-row">
                                          <span className="reply-sender">{m.sender_name}</span>
                                          <span className="reply-time">{m.time}</span>
                                        </div>
                                        <div className="reply-msg">{m.message}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="reply-input-box">
                                  <input
                                    type="text"
                                    placeholder="Write a reply..."
                                    value={state.replyInputs[comment.id] || ""}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) =>
                                      updateState({
                                        replyInputs: {
                                          ...state.replyInputs,
                                          [comment.id]: e.target.value
                                        }
                                      })
                                    }
                                  />
                                  <button
                                    className="reply-send-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSendReply(comment.id);
                                    }}
                                  >
                                    <i className="bi bi-send"></i>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>


                        ))
                      ) : (
                        <div className="draft-comments-empty">
                          <p>No comments found.</p>
                        </div>
                      )}
                    </div>

                    {/* FOOTER BUTTONS */}
                    <div className="draft-footer-actions">
                      <button
                        className="footer-btn save"
                        onClick={handleSaveNewVersion}
                      >
                        <i className="bi bi-save2 me-1" /> Save
                      </button>

                      <button
                        className="footer-btn send"
                        disabled={manageNegotiation?.is_sent === true}
                        onClick={() => {
                          const unresolved = comments.filter(c => !c.is_resolved);

                          if (unresolved.length > 0) {
                            toast.error("Please resolve all comments before sending.");
                            return;
                          }
                          // Check if content was changed but not saved
                          const hasUnsavedChanges = state.content !== state.originalContent && !state.versionSaved;
                          if (hasUnsavedChanges) {
                            toast.error("You have unsaved changes. Please save your new version before sending.");
                            return;
                          }
                          updateState({ isModalOpen: true });
                        }}
                      >
                        <i className="bi bi-send-fill me-1" /> Send
                      </button>

                    </div>
                  </aside>
                </div>
              </div>
            )}

            {/* ===== SUMMARY TAB ===== */}
            {activeTab === "summary" && (
              <SummarySidebar
                summaries={{
                  contract_summary: manageNegotiation?.summary || "",
                  contract_timelines:
                    manageNegotiation?.timelines || "",
                  contract_terms_conditions:
                    manageNegotiation?.terms_conditions || ""
                }}
                loading={negotiationLoading}
                isOpen={true}
                onToggle={() => updateState({ activeTab: "draft" })}
              />
            )}

            {/* ===== VERSIONS TAB ===== */}
            {activeTab === "versions" && (
              <div className="manage-negotiate-tab-content manage-versions-panel">
                {versionLoading ? (
                  <div className="page-loading">
                    <img src="/images/loader.gif" alt="Loading..." />
                  </div>
                ) : showComparison && comparisonData ? (
                  <VersionComparisonTab
                    comparisonData={comparisonData}
                  />
                ) : (
                  <div className="versions-empty">
                    <p>No comparison available.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ===== MODAL PREVIEW ===== */}
        <ModalComponent
          show={isModalOpen}
          handleClose={() => updateState({ isModalOpen: false })}
          title="Preview"
          size="lg"
          footerButtons={[
            {
              label: "Close",
              variant: "outline-secondary",
              onClick: () => updateState({ isModalOpen: false })
            },
            {
              label: "Send Now",
              variant: "primary",
              disabled: manageNegotiation?.is_sent === true,
              onClick: async () => {

                try {
                  await saveNegotiationNewVersion({
                    negotiation_id: manageNegotiation?.id,
                    html_content: content
                  });

                  await handleSendNegotiation();
                  updateState({ isModalOpen: false });
                  navigate("/manage/negotiation");
                } catch (err) {
                  toast.error("Send failed!");
                }
              },

            }
          ]}
        >
          <div
            id="a4-preview-container"
            style={{
              width: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "25px"
            }}
          >
            {state.loading ? (
              <div className="page-loading">
                <img src="/images/loader.gif" alt="Loading..." />
              </div>
            ) : (
              previewPages.map((pg, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    pageBreakAfter: "always"
                  }}
                >
                  <div
                    style={{
                      width: "210mm",
                      minHeight: "297mm",
                      padding: "20mm",
                      background: "#fff",
                      boxSizing: "border-box",
                      borderRadius: "6px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                      lineHeight: "1.6",
                      color: "#000",
                      marginBlock: "20px"
                    }}
                    dangerouslySetInnerHTML={{ __html: pg }}
                  />
                </div>
              )))}
          </div>
        </ModalComponent>
      </div>
    </>
  );
};

export default NegotiationManage;
